// packages/web/src/lib/api/endpoints.ts
// Centralized API endpoints registry for type safety and consistency

export const API_ENDPOINTS = {
  // Banking APIs
  banking: {
    balance: '/api/partners/banking/balance',
    balances: '/api/partners/banking/balances',
    platformCredits: '/api/partners/banking/platform-credits',
    transactions: '/api/partners/banking/transactions',
    deposit: '/api/partners/banking/deposit',
    withdraw: '/api/partners/banking/withdraw',
    status: '/api/partners/banking/status',
    connect: '/api/partners/banking/connect',
    onboard: '/api/partners/banking/onboard',
    autoReplenish: '/api/partners/banking/auto-replenish',
    recalculateEscrow: '/api/partners/banking/recalculate-escrow',
    withdrawalRequest: '/api/partners/banking/withdrawal/request',
    withdrawalHistory: '/api/partners/banking/withdrawal/history',
  },

  // Perks APIs
  perks: {
    list: '/api/partners/perks',
    deposits: '/api/partners/perks/deposits',
    depositTransactions: '/api/partners/perks/deposits/transactions',
    impact: '/api/partners/perks/impact',
    eligible: '/api/partners/perks/eligible',
    flagshipServices: '/api/partners/perks/flagship-services',
    riskAssessment: '/api/partners/perks/risk-assessment',
    bulkApply: '/api/partners/perks/bulk/apply',
    bulkPreview: '/api/partners/perks/bulk/preview',
  },

  // Commission APIs
  commission: {
    projections: '/api/commission/projections',
    dashboard: '/api/partners/commissions/dashboard',
    templates: '/api/partners/commission-templates',
    overrides: '/api/partners/commission-overrides',
    preview: '/api/commission/partners/preview',
    calculate: '/api/commission/calculate',
    estimates: '/api/commission/partners/estimates',
    fundsValidation: '/api/partners/funds-validation',
    deposit: '/api/commission/partners/deposit',
    depositSettings: '/api/commission/partners/deposit/settings',
  },

  // Orders APIs
  orders: {
    list: '/api/orders',
    partner: '/api/partners/orders',
    inbox: '/api/orders/inbox',
    queue: '/api/orders/queue',
    installs: '/api/orders/installs',
    manage: '/api/orders/manage',
  },

  // Services APIs
  services: {
    list: '/api/services',
    create: '/api/services',
    catalog: '/api/catalog/services',
  },

  // Catalog APIs
  catalog: {
    instances: '/api/catalog-instances',
    preview: '/api/partners/catalog/preview',
    subdomainReserve: '/api/partners/catalog/subdomain/reserve',
    subdomainUpdate: '/api/partners/catalog/subdomain',
    autocomplete: '/api/catalog/autocomplete',
  },

  // Settings APIs
  settings: {
    profile: '/api/partners/settings/profile',
    branding: '/api/partners/settings',
  },

  // Partners APIs
  partners: {
    profile: '/api/partners/profile',
  },

  // Brands APIs
  brands: {
    list: '/api/brands',
    create: '/api/brands',
  },

  // Tickets APIs
  tickets: {
    list: '/api/tickets',
  },

  // Marketplace APIs
  marketplace: {
    catalogs: '/api/marketplace/catalogs',
    services: '/api/marketplace/services',
  },

  // Super Admin APIs
  superAdmin: {
    partners: '/api/super-admin/partners',
    balances: '/api/super-admin/partners/balances',
    platformFees: '/api/super-admin/partners/:tenantId/platform-fees',
    fundAdjustments: '/api/super-admin/partners/:tenantId/adjust-funds',
    financialOverview: '/api/super-admin/financial-overview',
    services: '/api/super-admin/services',
    flagshipServices: '/api/super-admin/flagship-services',
    companies: '/api/super-admin/companies',
    branding: '/api/super-admin/platform/branding',
    rateLimits: '/api/super-admin/rate-limits/reset',
    settings: '/api/super-admin/settings',
  },

  // Partner Dashboard APIs
  partnerDashboard: {
    overview: '/api/partner-dashboard/overview',
    services: '/api/partner-dashboard/services',
    alerts: '/api/partner-dashboard/alerts',
    notifications: '/api/partner-dashboard/notifications',
    markRead: '/api/partner-dashboard/notifications/mark-all-read',
  },

  // Authentication APIs
  auth: {
    login: '/api/auth/login',
    logout: '/api/auth/logout',
    me: '/api/auth/me',
    register: '/api/auth/register',
  },

  // Ratings APIs
  ratings: {
    customer: '/api/customer-ratings',
    orders: '/api/orders?status=completed&can_rate=true',
  },

  // Geographic APIs
  geographic: {
    states: '/api/geographic/states',
    counties: '/api/geographic/counties',
    zipCodes: '/api/geographic/zip-codes',
    coverage: '/api/geographic/coverage',
  },

  // Cart APIs
  cart: {
    items: '/api/cart/items',
    auth: '/api/cart/auth',
    customer: '/api/customer/cart',
    checkout: '/api/customer/cart/checkout',
  },

  // Finance APIs
  finance: {
    currency: '/api/finance/currency',
    fxRate: '/api/finance/fx-rate',
    payouts: '/api/finance/payouts',
  },

  // Admin APIs (Phase 2: Comprehensive Super Admin)
  admin: {
    services: '/api/admin/services',
    orders: '/api/admin/orders',
    shutdown: '/api/admin/shutdown',
    catalogs: '/api/admin/catalogs',
    brands: '/api/admin/brands',
    partners: '/api/admin/partners',
    analytics: '/api/admin/analytics',
    interventions: '/api/admin/interventions',
  },

  // Monitoring APIs
  monitoring: {
    marketplaceSync: '/api/super-admin/monitoring/marketplace-sync',
  },

  // Feature APIs
  features: {
    list: '/api/features',
  },

  // Health APIs
  health: {
    check: '/api/health',
    buildInfo: '/api/health/build-info',
  },
} as const;

export type ApiEndpoint = typeof API_ENDPOINTS;
export type ApiEndpointPath = string;

// Helper function to get endpoint with parameter substitution
export function getEndpoint(path: string, params: Record<string, string> = {}): string {
  let result = path;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, value);
  });
  return result;
}

// Type-safe endpoint getter
export function getApiEndpoint<K extends keyof ApiEndpoint>(
  category: K,
  endpoint: keyof ApiEndpoint[K],
): string {
  return API_ENDPOINTS[category][endpoint] as string;
}
